/**
 * Reference data for the Assisted eKYC wizard — the lookups the retail journey pulls from
 * `meta/*` and `meta/application-setting/format/code/*` (contracts-1 §6.4, §6.5).
 *
 * Trimmed to what a demo needs: enough divisions/districts/thanas to exercise the cascade
 * without shipping the full Bangladesh administrative tree.
 */

export interface Division { divisionId: string; divisionName: string; divisionShortCode: string }
export interface District { districtId: string; districtName: string; districtShortCode: string; divisionId: string }
export interface Thana { thanaUpazilaId: string; name: string; shortCode: string; districtId: string }
export interface Branch { branchId: string; branchName: string; districtId: string }
export interface KeyValue { key: string; value: string }
export interface Sector { sectorCode: string; sectorName: string }
export interface OccupationCategory { occupationCode: string; occupationName: string }
export interface ProductType { productTypeCode: string; productTypeName: string; productTypeDescription: string; accountClass: string }

export const DIVISIONS: Division[] = [
  { divisionId: '1', divisionName: 'Dhaka', divisionShortCode: 'DHA' },
  { divisionId: '2', divisionName: 'Chattogram', divisionShortCode: 'CTG' },
  { divisionId: '3', divisionName: 'Khulna', divisionShortCode: 'KHU' },
  { divisionId: '4', divisionName: 'Rajshahi', divisionShortCode: 'RAJ' },
  { divisionId: '5', divisionName: 'Sylhet', divisionShortCode: 'SYL' },
];

export const DISTRICTS: District[] = [
  { districtId: '11', districtName: 'Dhaka', districtShortCode: 'DHK', divisionId: '1' },
  { districtId: '12', districtName: 'Gazipur', districtShortCode: 'GAZ', divisionId: '1' },
  { districtId: '13', districtName: 'Narayanganj', districtShortCode: 'NAR', divisionId: '1' },
  { districtId: '21', districtName: 'Chattogram', districtShortCode: 'CTG', divisionId: '2' },
  { districtId: '22', districtName: 'Cumilla', districtShortCode: 'CUM', divisionId: '2' },
  { districtId: '31', districtName: 'Khulna', districtShortCode: 'KHL', divisionId: '3' },
  { districtId: '41', districtName: 'Rajshahi', districtShortCode: 'RAJ', divisionId: '4' },
  { districtId: '51', districtName: 'Sylhet', districtShortCode: 'SYL', divisionId: '5' },
];

export const THANAS: Thana[] = [
  { thanaUpazilaId: '111', name: 'Motijheel', shortCode: 'MTJ', districtId: '11' },
  { thanaUpazilaId: '112', name: 'Dhanmondi', shortCode: 'DHM', districtId: '11' },
  { thanaUpazilaId: '113', name: 'Gulshan', shortCode: 'GLS', districtId: '11' },
  { thanaUpazilaId: '114', name: 'Mirpur', shortCode: 'MRP', districtId: '11' },
  { thanaUpazilaId: '121', name: 'Tongi', shortCode: 'TNG', districtId: '12' },
  { thanaUpazilaId: '131', name: 'Fatullah', shortCode: 'FTL', districtId: '13' },
  { thanaUpazilaId: '211', name: 'Agrabad', shortCode: 'AGR', districtId: '21' },
  { thanaUpazilaId: '212', name: 'Pahartali', shortCode: 'PHT', districtId: '21' },
  { thanaUpazilaId: '221', name: 'Cumilla Sadar', shortCode: 'CMS', districtId: '22' },
  { thanaUpazilaId: '311', name: 'Khulna Sadar', shortCode: 'KHS', districtId: '31' },
  { thanaUpazilaId: '411', name: 'Boalia', shortCode: 'BOA', districtId: '41' },
  { thanaUpazilaId: '511', name: 'Sylhet Sadar', shortCode: 'SYS', districtId: '51' },
];

export const BRANCHES: Branch[] = [
  { branchId: 'BR-0101', branchName: 'Motijheel Branch', districtId: '11' },
  { branchId: 'BR-0102', branchName: 'Gulshan Branch', districtId: '11' },
  { branchId: 'BR-0103', branchName: 'Dhanmondi Branch', districtId: '11' },
  { branchId: 'BR-0104', branchName: 'Mirpur Branch', districtId: '11' },
  { branchId: 'BR-0121', branchName: 'Tongi Branch', districtId: '12' },
  { branchId: 'BR-0201', branchName: 'Agrabad Branch', districtId: '21' },
  { branchId: 'BR-0221', branchName: 'Cumilla Branch', districtId: '22' },
  { branchId: 'BR-0301', branchName: 'Khulna Branch', districtId: '31' },
  { branchId: 'BR-0401', branchName: 'Rajshahi Branch', districtId: '41' },
  { branchId: 'BR-0501', branchName: 'Sylhet Branch', districtId: '51' },
];

export const RELIGIONS: KeyValue[] = [
  { key: 'Islam', value: 'ISLAM' },
  { key: 'Hinduism', value: 'HINDUISM' },
  { key: 'Buddhism', value: 'BUDDHISM' },
  { key: 'Christianity', value: 'CHRISTIANITY' },
  { key: 'Other', value: 'OTHER' },
];

export const PROFESSIONS: KeyValue[] = [
  { key: 'Service — Government', value: 'Service — Government' },
  { key: 'Service — Private', value: 'Service — Private' },
  { key: 'Service — Bank / NBFI', value: 'Service — Bank / NBFI' },
  { key: 'Business — Trading', value: 'Business — Trading' },
  { key: 'Business — Manufacturing', value: 'Business — Manufacturing' },
  { key: 'Student', value: 'Student' },
  { key: 'Homemaker', value: 'Homemaker' },
  { key: 'Retired', value: 'Retired' },
  { key: 'Farmer', value: 'Farmer' },
  { key: 'Freelancer', value: 'Freelancer' },
  { key: 'Gold / Jewellery Trader', value: 'Gold / Jewellery Trader' },
  { key: 'Money Changer', value: 'Money Changer' },
];

/** Two categories; each one decides the Occupation Options offered on Customer Information. */
export const OCCUPATION_CATEGORIES: OccupationCategory[] = [
  { occupationCode: 'OCC-BUSINESS', occupationName: 'Business' },
  { occupationCode: 'OCC-PROFESSION', occupationName: 'Profession' },
];

export const SECTORS: Sector[] = [
  { sectorCode: 'SEC-01', sectorName: 'Manufacturing' },
  { sectorCode: 'SEC-02', sectorName: 'Trade & Commerce' },
  { sectorCode: 'SEC-03', sectorName: 'Agriculture' },
  { sectorCode: 'SEC-04', sectorName: 'Service' },
  { sectorCode: 'SEC-05', sectorName: 'Construction' },
  { sectorCode: 'SEC-06', sectorName: 'Transport' },
  { sectorCode: 'SEC-07', sectorName: 'Others' },
];

export const INCOME_SOURCES: KeyValue[] = [
  { key: 'Salary', value: 'SALARY' },
  { key: 'Business income', value: 'BUSINESS' },
  { key: 'Agricultural income', value: 'AGRICULTURE' },
  { key: 'Rental income', value: 'RENTAL' },
  { key: 'Remittance', value: 'REMITTANCE' },
  { key: 'Investment / Dividend', value: 'INVESTMENT' },
  { key: 'Pension', value: 'PENSION' },
  { key: 'Family support', value: 'FAMILY_SUPPORT' },
];

export const NOMINEE_RELATIONS: KeyValue[] = [
  { key: 'Father', value: 'Father' },
  { key: 'Mother', value: 'Mother' },
  { key: 'Spouse', value: 'Spouse' },
  { key: 'Son', value: 'Son' },
  { key: 'Daughter', value: 'Daughter' },
  { key: 'Brother', value: 'Brother' },
  { key: 'Sister', value: 'Sister' },
  { key: 'Other', value: 'Other' },
];

export const ACCOUNT_PRODUCTS: ProductType[] = [
  { productTypeCode: 'PRD-SIMPLE', productTypeName: 'Simple Savings Account', productTypeDescription: '', accountClass: 'CONVENTIONAL' },
  { productTypeCode: 'PRD-PREMIUM', productTypeName: 'Premium Savings Account', productTypeDescription: '<p>Full-KYC savings account with no eKYC-specific caps and a debit card option.</p>', accountClass: 'CONVENTIONAL' },
  { productTypeCode: 'PRD-CURRENT', productTypeName: 'Current Account', productTypeDescription: '<p>Transactional account for business use. Full KYC only.</p>', accountClass: 'CONVENTIONAL' },
  { productTypeCode: 'PRD-ISL-SIMPLE', productTypeName: 'Al-Amin Simplified Savings', productTypeDescription: '<p>Shariah-compliant simplified savings under the Mudaraba principle.</p>', accountClass: 'ISLAMIC' },
  { productTypeCode: 'PRD-ISL-MUDARABA', productTypeName: 'Al-Amin Mudaraba Savings', productTypeDescription: '<p>Shariah-compliant profit-sharing savings account. Full KYC.</p>', accountClass: 'ISLAMIC' },
];

export const CARD_PRODUCTS: ProductType[] = [
  { productTypeCode: 'CARD-VISA-CLASSIC', productTypeName: 'Visa Classic Debit', productTypeDescription: '', accountClass: 'CONVENTIONAL' },
  { productTypeCode: 'CARD-VISA-GOLD', productTypeName: 'Visa Gold Debit', productTypeDescription: '', accountClass: 'CONVENTIONAL' },
  { productTypeCode: 'CARD-MC-TITANIUM', productTypeName: 'Mastercard Titanium Debit', productTypeDescription: '', accountClass: 'CONVENTIONAL' },
  { productTypeCode: 'CARD-ISL-CLASSIC', productTypeName: 'Al-Amin Debit Classic', productTypeDescription: '', accountClass: 'ISLAMIC' },
];

export const CHEQUE_LEAF_OPTIONS = [10, 20, 25, 50, 100];

export const CHEQUE_PRODUCTS: KeyValue[] = [
  { key: 'Savings 10-leaf', value: 'SAV-10' },
  { key: 'Savings 25-leaf', value: 'SAV-25' },
  { key: 'Current 50-leaf', value: 'CUR-50' },
  { key: 'Current 100-leaf', value: 'CUR-100' },
];

/**
 * Existing CBS accounts keyed by NID, used by Step 3's dedupe. Any NID not listed here behaves as
 * a brand-new customer, which is the common demo path.
 */
export interface ExistingAccount {
  accountNo: string;
  accountType: string;
  productType: string;
  accountStatus: string;
  branch: string;
  infoMismatch: boolean;
  mismatchDetails: { name: boolean; nid: boolean; passport: boolean; dob: boolean; motherName: boolean; fatherName: boolean };
  kycValidUntil: string;
}

export const EXISTING_ACCOUNTS: Record<string, ExistingAccount[]> = {
  // A clean existing customer with valid KYC — exercises the abbreviated flow.
  '1990123456789': [
    {
      accountNo: '0101330099001',
      accountType: 'Savings',
      productType: 'Premium Savings Account',
      accountStatus: 'Active',
      branch: 'Motijheel Branch',
      infoMismatch: false,
      mismatchDetails: { name: false, nid: false, passport: false, dob: false, motherName: false, fatherName: false },
      kycValidUntil: '2029-01-01',
    },
    {
      accountNo: '0057-0311000013',
      accountType: 'CASA',
      productType: 'CONVENTIONAL',
      accountStatus: 'NOWITHDRAWL',
      branch: 'Bijoy Nagar Branch',
      infoMismatch: false,
      mismatchDetails: { name: false, nid: false, passport: false, dob: false, motherName: false, fatherName: false },
      kycValidUntil: '2027-06-30',
    },
    {
      accountNo: '0072-0455000027',
      accountType: 'CASA',
      productType: 'ISLAMIC',
      accountStatus: 'Active',
      branch: 'Dilkusha Islamic Banking Branch',
      infoMismatch: false,
      mismatchDetails: { name: false, nid: false, passport: false, dob: false, motherName: false, fatherName: false },
      kycValidUntil: '2024-11-30',
    },
  ],
  // An existing customer whose CBS record disagrees with the NID — exercises the mismatch panel.
  '1985987654321': [
    {
      accountNo: '0101330099002',
      accountType: 'Savings',
      productType: 'Simple Savings Account',
      accountStatus: 'Active',
      branch: 'Gulshan Branch',
      infoMismatch: true,
      mismatchDetails: { name: false, nid: false, passport: false, dob: true, motherName: true, fatherName: false },
      kycValidUntil: '2024-01-01',
    },
  ],
};

/**
 * Dedupe fallback: the same customer records, keyed by mobile number.
 *
 * Exercises the SRS §M4 rule that CIF lookup is by NID *and* mobile — `01711000001` finds the
 * clean existing customer even when the NID typed does not match CBS.
 */
export const EXISTING_ACCOUNTS_BY_MOBILE: Record<string, ExistingAccount[]> = {
  '01711000001': EXISTING_ACCOUNTS['1990123456789'],
  '01711000002': EXISTING_ACCOUNTS['1985987654321'],
};
